import React, { Component } from 'react';
import PropTypes from 'prop-types';
import DataPoller from 'components/ui/DataPoller';
import CustomerActionCreators from 'actions/CustomersActionCreators';
import LocationPreview from 'components/ui/tailwind/location-preview';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import AccountContacts from 'components/v2/contacts';
import { INVOICE } from 'config/documents';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';
import { withTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { sluggify } from 'utils/localization/utils';
import SopDocuments from 'components/modules/sop/SopDocuments';
import ShipperSettings from './ShipperSettings';
import AssociateAccount from '../AssociateAccount';
import AccountDetails from './shipper-form';
import DefaultLocationForm from './default-location-form';
import InvoiceDocumentPreferencesForm from './InvoiceDocumentPreferencesForm';
import WebhooksList from '../../webhooks/List';
import RoutingConfiguration from '../../shipper/RoutingConfiguration';
import ShipperRoutingView from './shipper-mapping';
import FscSettings from './FscSettings';

const POLLING_FREQ = 10000;

const { value: INVOICE_SLUG } = INVOICE;

class AdminShipperDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      showInvoiceDocumentPreferencesModal: false,
      modalType: null,
      markets: [],
    };
  }

  componentDidMount() {
    this.fetchMarkets();
  }

  async fetchMarkets() {
    const markets = await this.getMarkets();
    this.setState({ markets });
  }

  getInvoiceDocumentPreferences() {
    const { details } = this.props;

    const currentPreferences = (details.invoice_document_preferences || []).map(
      (pref) => {
        if (pref.slug === 'invoice') {
          return {
            ...pref,
            slug: INVOICE_SLUG,
          };
        }
        return pref;
      },
    );

    if (!currentPreferences.find((pref) => pref.slug === INVOICE_SLUG)) {
      const sequence = currentPreferences.length + 1;
      return [...currentPreferences, { slug: INVOICE_SLUG, sequence }].sort(
        (a, b) => a.sequence - b.sequence,
      );
    }
    return currentPreferences.sort((a, b) => a.sequence - b.sequence);
  }
  getMarkets = async () => {
    if (!this.state.markets.length) {
      try {
        const res = await MarketsAPIUtils.getMarketsV2();
        if (res) {
          return res;
        }
        return [];
      } catch (e) {
        console.log('error getting markets', e.message);
        return [];
      }
    }
  };

  handleLocationRemove(field) {
    const updates = {
      [field]: null,
      _id: this.props.details._id,
    };
    this.props.onSaveDetails('Shipper', updates);
  }

  getUserAudits = () => {
    CustomerActionCreators.fetchCustomerAudits(this.props.details._id);
  };

  renderLocationEdit(type) {
    const { details, onSaveOrigin, onSaveDestination } = this.props;
    const location = type === 'Origin' ? details.origin : details.destination;
    const onSave = type === 'Origin' ? onSaveOrigin : onSaveDestination;
    const { t } = this.props;
    return (
      <DefaultLocationForm
        details={location}
        onSave={(data) => onSave(data)}
        title={t('DEFAULT_TYPE', { type: t(sluggify(type)) })}
      />
    );
  }

  onHideModal = () => {
    this.setState({ showModal: false, modalType: null });
  };

  renderModalComponent() {
    const { modalType } = this.state;

    if (modalType === 'Origin' || modalType === 'Destination') {
      return this.renderLocationEdit(modalType);
    }
  }

  render() {
    const { showModal } = this.state;
    const { details, t } = this.props;
    const stripe = details.stripe_customer_id || t('NO_STRIPE_ID');

    const invoiceDocumentPreferences = this.getInvoiceDocumentPreferences();

    const masterAccount = details.master_account || {};
    const shipperDetails = SessionStore.getUser();

    const breadcrumbs = {
      pages: [
        {
          name: details?.company_name || t('SHIPPER'),
          href: `/users/${details.id}`,
          current: true,
        },
      ],
    };

    const renderSidebar = () => (
      <ShipperSettings activePage="profile" userId={details.id} />
    );

    return (
      <LayoutWithSidebar sidebar={renderSidebar} breadcrumbs={breadcrumbs}>
        <DataPoller action={this.getUserAudits} frequency={POLLING_FREQ} />
        <div className="flex flex-wrap lg:-mx-6">
          <div className="w-full lg:w-1/3 lg:px-6">
            <AccountDetails
              details={details}
              onSave={(data) => this.props.onSaveDetails('Shipper', data)}
              onLogoUpdate={this.props.onLogoUpdate}
              onOpenPassword={this.props.onOpenPassword}
              onOpenJobs={this.props.onOpenJobs}
              onOpenAccountRequest={this.props.onOpenAccountRequest}
            />
            <div className="mt-6">
              <AssociateAccount
                userId={details.id}
                currentAssociation={masterAccount}
              />
            </div>
            <div className="">
              <LocationPreview
                onRemove={() => this.handleLocationRemove('origin')}
                type="Origin"
                location={details.origin}
                onEdit={() => {
                  this.setState({ showModal: true, modalType: 'Origin' });
                }}
              />
            </div>
            <div className="mt-6">
              <LocationPreview
                onRemove={() => this.handleLocationRemove('destination')}
                type="Destination"
                location={details.destination}
                onEdit={() => {
                  this.setState({
                    showModal: true,
                    modalType: 'Destination',
                  });
                }}
              />
            </div>
            <div className="mt-6">
              <FscSettings shipperId={details._id} />
            </div>
          </div>
          <div className="w-full lg:w-2/3 lg:px-6">
            <AccountContacts
              className="mb-6"
              contacts={details.contacts}
              userId={details._id}
            />
            <div className="my-6">
              <SopDocuments userId={details._id} />
            </div>

            <div className="mt-6">
              <CargomaticCard
                removeHorizontalPadding
                removeVerticalPadding
                header={() => (
                  <h3 className="my-0 text-xl font-medium">
                    {t('ROUTING_CONFIGURATION')}
                  </h3>
                )}
              >
                <RoutingConfiguration shipper={details} />
              </CargomaticCard>
            </div>

            <div className="my-6">
              <WebhooksList userId={details.id} accountType={details._type} />
            </div>
            <div className="my-6">
              <ShipperRoutingView
                shipper={details}
                markets={this.state.markets}
              />
            </div>
          </div>
        </div>
        {showModal && (
          <CargomaticModal
            show
            onHide={this.onHideModal}
            title={t('EDIT_LOCATION')}
            body={this.renderModalComponent()}
            footer={null}
            location="middle"
          />
        )}
      </LayoutWithSidebar>
    );
  }
}

AdminShipperDetails.propTypes = {
  details: PropTypes.object.isRequired,
  onOpenJobs: PropTypes.func.isRequired,
  onOpenPassword: PropTypes.func.isRequired,
  onLogoUpdate: PropTypes.func.isRequired,
  onSaveCreditCard: PropTypes.func.isRequired,
};

export default withTranslation()(AdminShipperDetails);
